import { CSSProperties, RefObject, useCallback, useMemo, useState } from 'react'
import { useIsomorphicLayoutEffect } from 'usehooks-ts'
import { isClient } from '~/utils/envChecks'

type StickyElementRef = RefObject<HTMLElement | null>

type StickyOffsetValues = {
  notificationHeight: number
  stickyBannersHeight: number
}

type StickyOffsetStyle = CSSProperties & {
  '--rp-sticky-notification-offset': string
  '--rp-sticky-banners-offset': string
  '--rp-sticky-banner-top': string
  '--rp-global-sticky-offset': string
}

const getElementHeight = (element: HTMLElement | null) =>
  Math.ceil(element?.getBoundingClientRect().height ?? 0)

const toPx = (value: number) => `${value}px`

export const buildGlobalStickyOffsetStyle = ({
  notificationHeight,
  stickyBannersHeight,
}: StickyOffsetValues): StickyOffsetStyle => {
  const notificationOffset = toPx(notificationHeight)
  const stickyBannersOffset = toPx(stickyBannersHeight)

  return {
    '--rp-sticky-notification-offset': notificationOffset,
    '--rp-sticky-banners-offset': stickyBannersOffset,
    '--rp-sticky-banner-top': notificationOffset,
    '--rp-global-sticky-offset': toPx(notificationHeight + stickyBannersHeight),
  }
}

export const useGlobalStickyOffsets = (
  notificationRef: StickyElementRef,
  stickyBannersRef: StickyElementRef
) => {
  const [offsets, setOffsets] = useState<StickyOffsetValues>({
    notificationHeight: 0,
    stickyBannersHeight: 0,
  })

  const updateOffsets = useCallback(() => {
    const nextOffsets = {
      notificationHeight: getElementHeight(notificationRef.current),
      stickyBannersHeight: getElementHeight(stickyBannersRef.current),
    }

    setOffsets((currentOffsets) =>
      currentOffsets.notificationHeight === nextOffsets.notificationHeight &&
      currentOffsets.stickyBannersHeight === nextOffsets.stickyBannersHeight
        ? currentOffsets
        : nextOffsets
    )
  }, [notificationRef, stickyBannersRef])

  useIsomorphicLayoutEffect(() => {
    if (isClient()) {
      updateOffsets()

      const observedElements = [notificationRef.current, stickyBannersRef.current].filter(
        (element): element is HTMLElement => Boolean(element)
      )

      if (typeof window.ResizeObserver === 'undefined') {
        window.addEventListener('resize', updateOffsets)
        return () => window.removeEventListener('resize', updateOffsets)
      }

      const observer = new window.ResizeObserver(updateOffsets)
      observedElements.forEach((element) => observer.observe(element))
      window.addEventListener('resize', updateOffsets)

      return () => {
        observer.disconnect()
        window.removeEventListener('resize', updateOffsets)
      }
    }

    return undefined
  }, [notificationRef, stickyBannersRef, updateOffsets])

  return useMemo(() => buildGlobalStickyOffsetStyle(offsets), [offsets])
}
